/**
 * Itinerary Service for FlowRide Frontend
 * Handles all itinerary-related API calls with persistent caching.
 * Provides methods for route planning, autocomplete, and itinerary management.
 */

import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";
import { buildStorage, type BuildStorage, type CacheRequestConfig, setupCache, type StorageValue } from "axios-cache-interceptor";
import localforage from "localforage";

import { convertGeocodeItemToSearchResultItem } from "../tools/GeocodeTools";
import { mapItineraryPricingPreferencesForApi } from "../tools/itineraryPricingPreferencesMapper.tools";
import { type UserChoice } from "../types/Forms";
import { type ItineraryCompletionRequestDTO, type ItineraryCompletionResponseDTO } from "../types/dtos/api/itinerary/itinerary-completion.dto";
import { type ItinerarySearchRequestDTO } from "../types/dtos/api/itinerary/itinerary-search-request.dto";
import { type ItinerarySearchResponseDTO } from "../types/dtos/api/itinerary/itinerary-search-response.dto";
import { type ItineraryDTO } from "../types/dtos/api/itinerary/itinerary.dto";
import { type RouteParametersDTO } from "../types/dtos/api/itinerary/route.dto";
import { type ItineraryPricingPreferencesDTO } from "../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import { type PricingEvaluationRequestDTO, type PricingEvaluationResponseDTO } from "../types/dtos/api/pricing/pricing-evaluation.dto";
import { type SalesOfferRequestDTO, type SalesOfferResponseDTO } from "../types/dtos/api/sales/sales-offer.dto";
import { type GeocodeItem, type GeocodeRequest, GeocodeType, SearchResultItem } from "../types/geocode-ui.types";
import {
    beginNewPlannerItineraryFlow,
    getOffRouteRecalculationFlowIdForRequests,
    getPlannerItineraryFlowIdForRequests,
} from "./http/correlationFlowStore";
import { registerFlowCorrelationInterceptors } from "./http/correlationItineraryAxios";
import type { FlowCorrelationScope } from "./http/flowCorrelationScope";

/** Request body for `POST /api/itinerary/realtime-enrich`. */
interface ItineraryRealtimeEnrichRequestBody { "itineraries": ItineraryDTO[] }

/** Response body for `POST /api/itinerary/realtime-enrich`. */
interface ItineraryRealtimeEnrichResponseBody { "itineraries": ItineraryDTO[] }

/** Available transportation modes for route planning */
export enum TransportMode {
    "FOOT" = "FOOT",
    "BIKE" = "BIKE",
    "RENTAL" = "RENTAL",
    "CARPOOL" = "CARPOOL",
    "TRANSIT" = "TRANSIT",
    "MYCAR" = "MYCAR",
}

export interface ItinerarySearchCallOptions {
    "signal"?:               AbortSignal;
    "flowCorrelationScope"?: FlowCorrelationScope;
}

/**
 * Persistent storage implementation using IndexedDB via localforage
 * Provides long-term caching for API responses
 */
const localForageStorage: BuildStorage = {
    async set( key: string, value: unknown ): Promise<void> {
        await localforage.setItem( key, value );
    },
    async remove( key: string ): Promise<void> {
        await localforage.removeItem( key );
    },
    async find( key: string ): Promise<StorageValue | undefined> {
        return await localforage.getItem( key ) as StorageValue | undefined;
    },
};

/** Persistent storage wrapper for axios cache */
const persistentStorage = buildStorage( localForageStorage );

/** Cache TTL for production builds (IndexedDB via localforage). Disabled in Vite dev (`import.meta.env.DEV`). */
const AXIOS_CACHE_TTL_MS = 1000 * 60 * 15;

const flowIdForPostCacheKey = ( request: CacheRequestConfig ): string => {
    const url = request.url ?? "";
    if ( url.includes( "/api/itinerary/search" ) ) {
        return request.flowCorrelationScope === "offRoute"
            ? getOffRouteRecalculationFlowIdForRequests() ?? ""
            : getPlannerItineraryFlowIdForRequests() ?? "";
    }
    if (
        url.includes( "/api/itinerary/completion" ) ||
        url.includes( "/api/itinerary/realtime-enrich" ) ||
        url.includes( "/api/pricing/evaluation" ) ||
        url.includes( "/api/sales/offer" )
    ) {
        return getPlannerItineraryFlowIdForRequests() ?? "";
    }

    return "";
};

/**
 * Axios instance: in dev, no cache (always hits the network). In production, persistent cache
 * with {@link AXIOS_CACHE_TTL_MS} lifetime for GET and POST.
 */
const axiosInstance: AxiosInstance = (
    import.meta.env.DEV
        ? axios.create()
        : setupCache( axios.create(), {
            "storage":     persistentStorage,
            "ttl":         AXIOS_CACHE_TTL_MS,
            "methods":     [ "get", "post" ],
            "generateKey": ( request: CacheRequestConfig ): string => {
                if ( request.method === "post" ) {
                    const flowKey = flowIdForPostCacheKey( request );

                    return request.url !== undefined ? `${ request.url }_flow:${ flowKey }_${ JSON.stringify( request.data ) }` : "";
                }

                return request.url ?? "";
            },
        } )
) as AxiosInstance;

registerFlowCorrelationInterceptors( axiosInstance );

const isRecord = ( value: unknown ): value is Record<string, unknown> => {
    return typeof value === "object" && value !== null;
};

const normalizeSearchResponse = ( data: unknown ): ItinerarySearchResponseDTO => {
    if ( isRecord( data ) === false ) {
        return {
            "itineraries":    {},
            "needCompletion": true,
        };
    }

    const nestedItineraries = data.itineraries;
    if ( isRecord( nestedItineraries ) ) {
        const needCompletion = typeof data.needCompletion === "boolean"
            ? data.needCompletion
            : true;

        return {
            "itineraries": nestedItineraries as Record<string, ItineraryDTO>,
            needCompletion,
        };
    }

    // Backward compatibility: old payload was directly Record<string, ItineraryDTO>.
    return {
        "itineraries":    data as Record<string, ItineraryDTO>,
        "needCompletion": true,
    };
};

/**
 * Itinerary Service with persistent caching
 * Handles all itinerary-related operations with optimized caching
 */
export class ItineraryService {

    /**
     * Geocode a location query (forward geocoding)
     * @param data - Geocoding request with text and place coordinates
     * @returns Promise with geocoding results
     */
    async geocode( data: GeocodeRequest, signal?: AbortSignal ) {
        const params = new URLSearchParams( {
            "text":  data.text,
            "place": `${ data.place.lat },${ data.place.lon }`,
        } );

        const response = await axiosInstance.get( `/api/geocode?${ params.toString() }`, { signal } );

        return {
            ...response,
            "data": ( response.data as GeocodeItem[] ).map( convertGeocodeItemToSearchResultItem ),
        };
    }

    /**
     * Geocode using fallback provider only
     * @param data - Geocoding request with text and place coordinates
     * @returns Promise with geocoding results from fallback provider
     */
    async geocodeFallback( data: GeocodeRequest, signal?: AbortSignal ) {
        const params = new URLSearchParams( {
            "text":            data.text,
            "place":           `${ data.place.lat },${ data.place.lon }`,
            "useFallbackOnly": "true",
        } );

        const response = await axiosInstance.get( `/api/geocode?${ params.toString() }`, { signal } );

        return {
            ...response,
            "data": ( response.data as GeocodeItem[] ).map( convertGeocodeItemToSearchResultItem ),
        };
    }

    /**
     * Reverse geocode coordinates to address
     * @param lat - Latitude coordinate
     * @param lon - Longitude coordinate
     * @returns Promise with reverse geocoding results
     */
    async reverseGeocode( lat: number, lon: number, type: GeocodeType | "ALL" = "ALL" ): Promise<SearchResultItem[]> {
        const params = new URLSearchParams( {
            "place": `${ lat },${ lon }`,
            "type":  type,
        } );

        const response = await axiosInstance.get( `/api/geocode/reverse?${ params.toString() }` );

        return ( response.data as GeocodeItem[] ).map( convertGeocodeItemToSearchResultItem );
    }

    /**
     * Search for itineraries using the new search endpoint
     * @param data - Itinerary search request
     * @param options - Optional abort signal and flow scope (`planner` default; `offRoute` for recalculation).
     * @returns Promise with array of itinerary DTOs
     */
    async searchItinerary(
        data: ItinerarySearchRequestDTO,
        options?: ItinerarySearchCallOptions,
    ): Promise<ItinerarySearchResponseDTO> {
        const requestModes: ItinerarySearchRequestDTO["modes"] = [ ...data.modes ];

        // Build request body
        const requestBody: ItinerarySearchRequestDTO = {
            "from": data.from,
            "to":   data.to,
            "time": {
                "timestamp":            data.time.timestamp,
                "isDepartureTimestamp": data.time.isDepartureTimestamp,
                ...data.time.timezone !== undefined ? { "timezone": data.time.timezone } : {},
            },
            "modes": requestModes,
            ...data.currency !== undefined ? { "currency": data.currency } : {},
            ...data.havePersonalBike !== undefined ? { "havePersonalBike": data.havePersonalBike } : {},
            ...data.rentalVehicleTypes !== undefined ? { "rentalVehicleTypes": data.rentalVehicleTypes } : {},
            ...data.bikeSpeedKmh !== undefined ? { "bikeSpeedKmh": data.bikeSpeedKmh } : {},
            ...data.elevationCosts !== undefined ? { "elevationCosts": data.elevationCosts } : {},
            ...data.transportPersonalBike !== undefined ? { "transportPersonalBike": data.transportPersonalBike } : {},
            ...data.tripEndByBike !== undefined ? { "tripEndByBike": data.tripEndByBike } : {},
            ...data.carItineraryType !== undefined ? { "carItineraryType": data.carItineraryType } : {},
            ...data.carDropOff !== undefined ? { "carDropOff": data.carDropOff } : {},
            ...data.carParkAndRide !== undefined ? { "carParkAndRide": data.carParkAndRide } : {},
            ...data.transitModeFilters !== undefined ? { "transitModeFilters": data.transitModeFilters } : {},
            ...data.maxTransfers !== undefined ? { "maxTransfers": data.maxTransfers } : {},
        };

        const scope: FlowCorrelationScope = options?.flowCorrelationScope === "offRoute" ? "offRoute" : "planner";
        const config: AxiosRequestConfig = {
            "signal":               options?.signal,
            "flowCorrelationScope": scope,
        };

        const response = await axiosInstance.post(
            "/api/itinerary/search",
            requestBody,
            config,
        );

        return normalizeSearchResponse( response.data );
    }

    async completeItineraries(
        data: ItineraryCompletionRequestDTO,
        signal?: AbortSignal,
    ): Promise<ItineraryCompletionResponseDTO> {
        const config: AxiosRequestConfig = { signal };
        const response = await axiosInstance.post(
            "/api/itinerary/completion",
            data,
            config,
        );

        return response.data as ItineraryCompletionResponseDTO;
    }

    async evaluateItinerariesPricing(
        itineraries: Record<string, ItineraryDTO>,
        pricingPreferences: ItineraryPricingPreferencesDTO,
        signal?: AbortSignal,
    ): Promise<PricingEvaluationResponseDTO> {
        const mappedPreferences = mapItineraryPricingPreferencesForApi( pricingPreferences );
        if ( mappedPreferences === undefined ) {
            throw new Error( "Pricing preferences are required for /api/pricing/evaluation" );
        }
        const requestBody: PricingEvaluationRequestDTO = {
            itineraries,
            "pricingPreferences": mappedPreferences,
        };

        const config: AxiosRequestConfig = { signal };
        const response = await axiosInstance.post(
            "/api/pricing/evaluation",
            requestBody,
            config,
        );

        return response.data as PricingEvaluationResponseDTO;
    }

    async evaluateItineraryOffer(
        itinerary: ItineraryDTO,
        pricingPreferences: ItineraryPricingPreferencesDTO,
        signal?: AbortSignal,
    ): Promise<SalesOfferResponseDTO> {
        const mappedPreferences = mapItineraryPricingPreferencesForApi( pricingPreferences );
        const requestBody: SalesOfferRequestDTO = {
            itinerary,
            ...mappedPreferences !== undefined ? { "pricingPreferences": mappedPreferences } : {},
        };
        const config: AxiosRequestConfig = { signal };
        const response = await axiosInstance.post(
            "/api/sales/offer",
            requestBody,
            config,
        );

        return response.data as SalesOfferResponseDTO;
    }

    /**
     * Enriches saved itineraries with live delays and platforms (ephemeral overlay).
     *
     * Starts a new planner `x-flow-id` for the batch so correlation stays independent
     * from the last OD search. Does not persist results.
     *
     * @param itineraries Canonical itineraries to enrich (typically current + upcoming saved).
     * @param signal Optional abort signal for unmount / superseded polls.
     * @returns Enriched itineraries in the same order as the request (best-effort).
     */
    async enrichSavedItinerariesRealtime(
        itineraries: ItineraryDTO[],
        signal?: AbortSignal,
    ): Promise<ItineraryDTO[]> {
        beginNewPlannerItineraryFlow();

        const requestBody: ItineraryRealtimeEnrichRequestBody = { itineraries };
        const config: AxiosRequestConfig = { signal };
        const response = await axiosInstance.post(
            "/api/itinerary/realtime-enrich",
            requestBody,
            config,
        );
        const body = response.data as ItineraryRealtimeEnrichResponseBody;

        return Array.isArray( body.itineraries ) ? body.itineraries : itineraries;
    }

    /**
     * Get car itinerary using the car-specific endpoint
     * @param data - Route parameters for car itinerary
     * @returns Promise with car itinerary response
     */
    async getCarItinerary( data: RouteParametersDTO ) {
        return await axiosInstance.post( "/api/itinerary/car", data );
    }

    async saveUserTrip( data: UserChoice ) {
        return await axiosInstance.post( "/api/itinerary/saveusertrip", data );
    }

}
